import React, { useState } from 'react';
import { X, Calendar, FileText, Users, Mail, Video, Target, AlertTriangle, Loader } from 'lucide-react';

export interface GoogleScopeSelection {
  calendar: boolean;
  drive: boolean;
  contacts: boolean;
  gmail: boolean;
  meet: boolean;
  ads: boolean;
}

interface GoogleConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selection: GoogleScopeSelection) => Promise<void> | void;
  initialSelection?: GoogleScopeSelection;
  alreadyConnected?: boolean;
}

const DEFAULT_SELECTION: GoogleScopeSelection = {
  calendar: true,
  drive: true,
  contacts: true,
  gmail: false,
  meet: false,
  ads: false,
};

export function GoogleConnectModal({
  isOpen,
  onClose,
  onConfirm,
  initialSelection,
  alreadyConnected = false,
}: GoogleConnectModalProps) {
  const [selection, setSelection] = useState<GoogleScopeSelection>(
    initialSelection || DEFAULT_SELECTION
  );
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const toggle = (key: keyof GoogleScopeSelection) => {
    setSelection(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const atLeastOne = selection.calendar || selection.drive || selection.contacts || selection.gmail || selection.meet || selection.ads;

  const handleConfirm = async () => {
    if (!atLeastOne) {
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(selection);
    } finally {
      setSubmitting(false);
    }
  };

  const hasBeta = selection.gmail || selection.meet || selection.ads;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 dark:bg-black/60 p-4">
      <div className="w-full max-w-lg bg-white dark:bg-[#191919] rounded-xl shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <div>
            <h2 className="text-base font-semibold text-[#37352F] dark:text-white">
              {alreadyConnected ? 'Modifica permessi Google' : 'Connetti il tuo account Google'}
            </h2>
            <p className="text-xs text-[#787774] dark:text-white/50 mt-0.5">
              Scegli quali servizi vuoi sincronizzare con Flusia
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-1.5 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 text-[#787774] dark:text-white/60 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-2 max-h-[60vh] overflow-y-auto">
          <ScopeItem
            icon={Calendar}
            title="Google Calendar"
            description="Sincronizza eventi e attività con il tuo calendario"
            checked={selection.calendar}
            onChange={() => toggle('calendar')}
          />
          <ScopeItem
            icon={FileText}
            title="Google Drive"
            description="Salva documenti dei clienti su Drive"
            checked={selection.drive}
            onChange={() => toggle('drive')}
          />
          <ScopeItem
            icon={Users}
            title="Google Contacts"
            description="Importa i tuoi contatti Google"
            checked={selection.contacts}
            onChange={() => toggle('contacts')}
          />
          <ScopeItem
            icon={Mail}
            title="Gmail"
            description="Leggi e invia email dal CRM"
            checked={selection.gmail}
            onChange={() => toggle('gmail')}
            beta
          />
          <ScopeItem
            icon={Video}
            title="Meet — Trascrizioni"
            description="Importa automaticamente le trascrizioni dei meeting"
            checked={selection.meet}
            onChange={() => toggle('meet')}
            beta
          />
          <ScopeItem
            icon={Target}
            title="Google Ads"
            description="Sincronizza campagne, lead e statistiche Google Ads"
            checked={selection.ads}
            onChange={() => toggle('ads')}
            beta
          />

          {hasBeta && (
            <div className="mt-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                  <p className="font-medium mb-1">Funzionalità in beta</p>
                  <p>
                    Gmail, Meet Trascrizioni e Google Ads richiedono scope sensibili in attesa di verifica da Google. Durante la connessione vedrai un avviso di sicurezza.
                  </p>
                  <p className="mt-1">
                    Clicca <span className="font-medium">"Avanzate"</span> → <span className="font-medium">"Vai a Flusia (non sicuro)"</span> per procedere.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-[rgba(55,53,47,0.02)] dark:bg-white/[0.02]">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 rounded-md transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || !atLeastOne}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#37352F] dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-md hover:bg-[#37352F]/90 dark:hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting && <Loader className="h-3.5 w-3.5 animate-spin" />}
            {alreadyConnected ? 'Aggiorna permessi' : 'Connetti Google'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScopeItem({
  icon: Icon,
  title,
  description,
  checked,
  onChange,
  beta = false,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  checked: boolean;
  onChange: () => void;
  beta?: boolean;
}) {
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
        checked
          ? 'border-[#2383E2] bg-[#2383E2]/5'
          : 'border-[rgba(55,53,47,0.09)] dark:border-white/10 hover:bg-[rgba(55,53,47,0.02)] dark:hover:bg-white/[0.02]'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-1 h-4 w-4 rounded border-gray-300 text-[#2383E2] focus:ring-[#2383E2] flex-shrink-0"
      />
      <div className="p-1.5 rounded-md bg-[rgba(55,53,47,0.04)] dark:bg-white/5 flex-shrink-0">
        <Icon className="h-4 w-4 text-[#37352F] dark:text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#37352F] dark:text-white">{title}</span>
          {beta && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 font-semibold uppercase tracking-wide">
              Beta
            </span>
          )}
        </div>
        <p className="text-xs text-[#787774] dark:text-white/50 mt-0.5">{description}</p>
      </div>
    </label>
  );
}
